import React, { FC, memo, useMemo } from 'react';

import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import Typography from '@mui/material/Typography';
import { ColumnDef } from '@tanstack/react-table';

import { formatBNWithCommas } from '@utils/formaters.utils';

import Box from '@components/common/Box';
import Button from '@components/common/Button';
import Table from '@components/common/Table';
import {
  BASE_TABLE_HEIGHT,
  ROW_HEIGHT,
} from '@components/common/Table/table.constants';
import { getCellStyles } from '@components/common/Table/table.helpers';
import Counter from '@components/vaults/Counter';
import FarmDetails from '@components/vaults/FarmDetails';
import RiskLeveler from '@components/vaults/RiskLeveler';

import { PERCENT_DIVIDER } from '@constants/common/common.constants';
import { Size, TableVariant, Variant } from '@constants/common/ui.constants';
import { V3_TOKENS_TYPE } from '@constants/vaults/tokens.constants';
import {
  CREATE_VAULT_HEADER,
  CreateVaultsKeys,
  MAX_FARM_PERCENT,
  MIN_FARM_PERCENT,
} from '@constants/vaults/vault.constants';

import { TokensDto } from '@dto/vaults/tokens.dto';

import { FarmCard } from '@app-types/vaults/farm.types';

import useStyles from './styles.module';

interface Props {
  tokens: TokensDto;
  isSideBarView?: boolean;
  selectedFarms: FarmCard[];
  tableHeight?: number;
  onDeleteFarm: (value: number) => void;
  onChangeFarmCount?: (id: number, percent: number) => void;
}

const SelectedFarms: FC<Props> = ({
  selectedFarms,
  tokens,
  tableHeight,
  onChangeFarmCount,
  isSideBarView = false,
  onDeleteFarm,
}) => {
  const { classes } = useStyles({ hasFarms: !!selectedFarms.length });

  const columns = useMemo<ColumnDef<FarmCard>[]>(
    () => [
      {
        size: 270,
        id: CreateVaultsKeys.Farm,
        accessorKey: CreateVaultsKeys.Farm,
        header: () => CREATE_VAULT_HEADER[CreateVaultsKeys.Farm],
        cell: (info) => getCellStyles(info.getValue<string>()),
        accessorFn: ({ farm }) => {
          const {
            chainId,
            stakingToken,
            stakingTokenType,
            dateOfAdd,
            poolV3Fee,
            poolV3,
            enabled,
            dexFactory,
          } = farm;

          const address = V3_TOKENS_TYPE.includes(stakingTokenType)
            ? poolV3
            : stakingToken;
          const factoryTokens = tokens.farm[chainId];
          const stakeToken = factoryTokens[address as string];
          const enabledFarmData = enabled
            ? { origin, dexFactoryName: dexFactory.name }
            : undefined;

          return (
            <div className={classes.farm}>
              <FarmDetails
                withChainLabel
                chainId={chainId}
                stakingToken={stakeToken}
                isPoolV3PositionOutRange={false}
                enabledFarmData={enabledFarmData}
                farmTagsData={{ dateOfAdd, poolV3Fee }}
              />
            </div>
          );
        },
      },

      {
        size: 120,
        id: CreateVaultsKeys.Exchange,
        accessorKey: CreateVaultsKeys.Exchange,
        header: () => CREATE_VAULT_HEADER[CreateVaultsKeys.Exchange],
        cell: (info) => getCellStyles(info.getValue<string>()),
        accessorFn: (row) => row.farm.dexFactory.name,
      },
      {
        size: 130,
        id: CreateVaultsKeys.Liquidity,
        accessorKey: CreateVaultsKeys.Liquidity,
        header: () => CREATE_VAULT_HEADER[CreateVaultsKeys.Liquidity],
        cell: (info) => getCellStyles(info.getValue<string>()),
        accessorFn: (row) => `$${formatBNWithCommas(row.farm.tvlUsd, 2)}`,
      },
      {
        size: 130,
        id: CreateVaultsKeys.APR,
        accessorKey: CreateVaultsKeys.APR,
        header: () => CREATE_VAULT_HEADER[CreateVaultsKeys.APR],
        cell: (info) => getCellStyles(info.getValue<string>()),
        accessorFn: (row) =>
          `${formatBNWithCommas(Number(row.farm.apr) * PERCENT_DIVIDER, 2)}%`,
      },
      {
        size: 60,
        id: CreateVaultsKeys.Risk,
        accessorKey: CreateVaultsKeys.Risk,
        header: () => CREATE_VAULT_HEADER[CreateVaultsKeys.Risk],
        cell: (info) => getCellStyles(info.getValue<string>()),
        accessorFn: (row) => (
          <RiskLeveler
            value={row.farm.riskLevel}
            className={classes.riskLeveler}
          />
        ),
      },
      {
        size: isSideBarView ? 0 : 160,
        className: 'right',
        meta: { style: { textAlign: 'center', justifyContent: 'center' } },
        id: CreateVaultsKeys.Allocation,
        accessorKey: CreateVaultsKeys.Allocation,
        header: () =>
          isSideBarView ? '' : CREATE_VAULT_HEADER[CreateVaultsKeys.Allocation],
        cell: (info) => getCellStyles(info.getValue<string>()),
        accessorFn: ({ percent, farm }) =>
          isSideBarView ? null : (
            <Counter
              className={classes.counter}
              minValue={MIN_FARM_PERCENT}
              maxValue={MAX_FARM_PERCENT}
              count={percent}
              onChange={(val) =>
                onChangeFarmCount && onChangeFarmCount(farm.id, val)
              }
              onCount={(val) =>
                onChangeFarmCount && onChangeFarmCount(farm.id, val)
              }
              inputValue={percent}
              suffix="%"
            />
          ),
      },
      {
        size: 60,
        id: CreateVaultsKeys.Action,
        accessorKey: CreateVaultsKeys.Action,
        header: () => CREATE_VAULT_HEADER[CreateVaultsKeys.Action],
        cell: (info) => getCellStyles(info.getValue<string>()),
        accessorFn: ({ farm }) => (
          <div className={classes.btnBox}>
            <Button
              variant={Variant.Square}
              size={Size.Small}
              onClick={() => onDeleteFarm(farm.id)}
            >
              <DeleteOutlineOutlinedIcon />
            </Button>
          </div>
        ),
      },
    ],
    [isSideBarView, onChangeFarmCount, onDeleteFarm, tokens],
  );

  const selectedFarmsTableHeight = useMemo(
    () => tableHeight || BASE_TABLE_HEIGHT + ROW_HEIGHT * selectedFarms.length,
    [tableHeight, selectedFarms],
  );

  return (
    <Box title="Selected Farms" classNameChildren={classes.box}>
      {selectedFarms.length ? (
        <Table
          data={selectedFarms}
          columns={columns}
          rowHeight={ROW_HEIGHT}
          tableHeight={selectedFarmsTableHeight}
          isLoading={false}
          totalRowCount={selectedFarms.length}
          variant={TableVariant.Secondary}
          pinnedRightColumn={[CreateVaultsKeys.Action]}
        />
      ) : (
        <Typography variant="caption1" color={(theme) => theme.secondary.s3}>
          Please select a farm from the list
        </Typography>
      )}
    </Box>
  );
};

export default memo(SelectedFarms);
